import type { OpsStatus, OpsStatusPipeline, SystemFlow } from '../types';
import { classifyRoute } from './classifyRoute';
import type {
  BoardDestination,
  BoardFeed,
  BoardPlatform,
  BoardRoute,
  RoutingBoardModel,
  WorkspaceApiLike,
} from './types';

export type ComposeRoutingBoardInput = {
  ops?: OpsStatus | null;
  workspaceApis?: WorkspaceApiLike[] | null;
  systemFlows?: SystemFlow[] | null;
  scopedAppId?: string | null;
  now?: number;
};

function matchesSeat(appId: string | null | undefined, scopedAppId?: string | null): boolean {
  if (!scopedAppId) return true;
  return String(appId || '') === scopedAppId;
}

function feedIdForPipeline(pipeline: OpsStatusPipeline): string {
  return String(pipeline.pipeline_id || pipeline.api_id || '').trim();
}

function toFeedFromPipeline(pipeline: OpsStatusPipeline, now?: number): BoardFeed | null {
  const apiId = String(pipeline.api_id || '').trim();
  const id = feedIdForPipeline(pipeline);
  if (!apiId && !id) return null;
  const classified = classifyRoute({
    last_success_at: pipeline.last_success_at,
    last_error: pipeline.last_error,
    health: pipeline.health,
    display_status: pipeline.display_status,
    interval_seconds: pipeline.interval_seconds,
    now,
  });
  return {
    id: id || apiId,
    api_id: apiId || id,
    pipeline_id: pipeline.pipeline_id || null,
    name: pipeline.api_name || apiId || id,
    endpoint_id: pipeline.endpoint_id ?? null,
    interval_seconds: pipeline.interval_seconds ?? null,
    last_success_at: pipeline.last_success_at ?? null,
    last_error: pipeline.last_error ?? null,
    records_24h: pipeline.records_24h ?? null,
    census_store: pipeline.census_store ?? null,
    health: pipeline.health ?? null,
    status: pipeline.status ?? null,
    app_id: pipeline.app_id ?? null,
    lane: classified.lane,
    pulse: classified.pulse,
  };
}

function toFeedFromWorkspace(api: WorkspaceApiLike, now?: number): BoardFeed | null {
  const apiId = String(api.api_id || '').trim();
  if (!apiId) return null;
  const classified = classifyRoute({
    last_success_at: api.runtime?.latest_ingest_at,
    last_error: api.runtime?.last_error,
    health: api.runtime?.lane === 'broken' ? 'error' : api.runtime?.lane === 'live' ? 'healthy' : null,
    interval_seconds: null,
    now,
  });
  return {
    id: apiId,
    api_id: apiId,
    pipeline_id: null,
    name: api.api_name || apiId,
    endpoint_id: null,
    interval_seconds: null,
    last_success_at: api.runtime?.latest_ingest_at ?? null,
    last_error: api.runtime?.last_error ?? null,
    records_24h: api.runtime?.records_24h ?? null,
    census_store: api.runtime?.census_store ?? null,
    health: api.runtime?.lane ?? null,
    status: api.status ?? null,
    app_id: null,
    lane: classified.lane,
    pulse: classified.pulse,
  };
}

function destinationFromSystemFlow(flow: SystemFlow): BoardDestination | null {
  const id = String(flow.id || '').trim();
  if (!id) return null;
  const meta = (flow.metadata || {}) as { category?: string; pipeline_id?: string };
  const category = meta.category || null;
  const entityLabel = flow.entity?.label || flow.entity?.id || null;
  const label = entityLabel
    ? `${flow.entity?.type ? `${flow.entity.type}: ` : ''}${entityLabel}`
    : (flow.name || id);
  return {
    id,
    kind: 'system_flow',
    label,
    store: null,
    flow_type: flow.flow_type || null,
    category,
    description: flow.description || null,
  };
}

function isIngestDestination(flow: SystemFlow): boolean {
  const meta = (flow.metadata || {}) as { category?: string };
  return meta.category === 'api_ingest' || flow.flow_type === 'api_ingest';
}

function destLane(feeds: BoardFeed[]): { lane: BoardFeed['lane']; pulse: boolean } {
  if (feeds.some((feed) => feed.lane === 'live')) return { lane: 'live', pulse: true };
  if (feeds.length > 0 && feeds.every((feed) => feed.lane === 'broken')) {
    return { lane: 'broken', pulse: false };
  }
  return { lane: 'quiet', pulse: false };
}

/**
 * Client-side board summary from existing Data Flow / ingest reads.
 * No invented business fields. Empty inputs produce an empty board.
 */
export function composeRoutingBoard(input: ComposeRoutingBoardInput): RoutingBoardModel {
  const now = input.now;
  const scopedAppId = input.scopedAppId || null;
  const pipelines = (input.ops?.api_pipelines?.pipelines || []).filter((pipeline) =>
    matchesSeat(pipeline.app_id, scopedAppId)
  );

  const feeds: BoardFeed[] = [];
  const seenApiIds = new Set<string>();

  for (const pipeline of pipelines) {
    const feed = toFeedFromPipeline(pipeline, now);
    if (!feed) continue;
    feeds.push(feed);
    seenApiIds.add(feed.api_id);
  }

  for (const api of input.workspaceApis || []) {
    const apiId = String(api.api_id || '').trim();
    if (!apiId || seenApiIds.has(apiId)) continue;
    const feed = toFeedFromWorkspace(api, now);
    if (!feed) continue;
    feeds.push(feed);
    seenApiIds.add(apiId);
  }

  const counts = {
    all: feeds.length,
    live: feeds.filter((feed) => feed.lane === 'live').length,
    quiet: feeds.filter((feed) => feed.lane === 'quiet').length,
    broken: feeds.filter((feed) => feed.lane === 'broken').length,
  };

  if (feeds.length === 0) {
    return { feeds: [], platform: null, destinations: [], routes: [], counts };
  }

  const censusStore =
    feeds.map((feed) => feed.census_store).find((store) => Boolean(store))
    || input.ops?.data_volume?.census_store
    || null;

  const platform: BoardPlatform = {
    id: 'platform-ingest',
    label: 'This seat ingest',
    store: censusStore,
    records_24h: input.ops?.data_volume?.ingest_last_24h ?? null,
    total: input.ops?.data_volume?.ingest_history_total ?? null,
  };

  const destinations: BoardDestination[] = [];
  if (censusStore) {
    destinations.push({
      id: `dest-store-${censusStore}`,
      kind: 'history_store',
      label: censusStore,
      store: censusStore,
      flow_type: null,
      category: null,
      description: 'Same store as Data Flow 24h census',
    });
  }

  for (const flow of input.systemFlows || []) {
    if (!isIngestDestination(flow)) continue;
    const dest = destinationFromSystemFlow(flow);
    if (dest) destinations.push(dest);
  }

  const outgoing = destLane(feeds);
  const routes: BoardRoute[] = [];
  for (const feed of feeds) {
    routes.push({
      id: `route-${feed.id}-platform`,
      fromId: feed.id,
      toId: platform.id,
      lane: feed.lane,
      pulse: feed.pulse,
      feedId: feed.id,
      last_success_at: feed.last_success_at,
      last_error: feed.last_error,
      records_24h: feed.records_24h,
      interval_seconds: feed.interval_seconds,
    });
  }
  for (const dest of destinations) {
    routes.push({
      id: `route-platform-${dest.id}`,
      fromId: platform.id,
      toId: dest.id,
      lane: outgoing.lane,
      pulse: outgoing.pulse,
      feedId: feeds[0]?.id || platform.id,
      last_success_at: feeds.find((feed) => feed.last_success_at)?.last_success_at ?? null,
      last_error: feeds.find((feed) => feed.last_error)?.last_error ?? null,
      records_24h: platform.records_24h ?? null,
      interval_seconds: null,
    });
  }

  return { feeds, platform, destinations, routes, counts };
}

export function filterBoard(model: RoutingBoardModel, filter: 'all' | 'live' | 'quiet' | 'broken'): RoutingBoardModel {
  if (filter === 'all') return model;
  const feeds = model.feeds.filter((feed) => feed.lane === filter);
  if (feeds.length === 0) {
    return {
      ...model,
      feeds: [],
      platform: null,
      destinations: [],
      routes: [],
    };
  }
  const feedIds = new Set(feeds.map((feed) => feed.id));
  const outgoing = destLane(feeds);
  const routes = model.routes
    .filter((route) => feedIds.has(route.fromId) || route.fromId === model.platform?.id)
    .map((route) => (
      route.fromId === model.platform?.id
        ? { ...route, lane: outgoing.lane, pulse: outgoing.pulse }
        : route
    ));
  return {
    ...model,
    feeds,
    routes,
  };
}
